import { Link } from 'react-router-dom'

import type { ActivityEntry } from '@/api/automation'
import { NotLivePage, useNotLivePage } from '@/app/notLive'
import { Card, DataTable, EmptyState, formatRelativeTime, LoadingBlock, useNow, type Column } from '@/design'
import type { DrillDown } from '@/lib/drilldown'
import { useFilterSearch } from '@/lib/useGlobalFilters'

import './automation.css'
import { ActivityOutcomeBadge } from './badges'
import { useAutomationQuery, useDrillDown } from './hooks'

function columns(now: Date, href: (target: DrillDown) => string, search: string): Column<ActivityEntry>[] {
  return [
    {
      key: 'workflow',
      header: 'Workflow',
      sortValue: (row) => row.workflowName,
      cell: (row) => <Link to={`/automation/workflows/${row.workflowId}${search}`}>{row.workflowName}</Link>,
    },
    { key: 'at', header: 'When', sortValue: (row) => row.at, cell: (row) => formatRelativeTime(row.at, now) },
    { key: 'outcome', header: 'Outcome', cell: (row) => <ActivityOutcomeBadge outcome={row.outcome} /> },
    { key: 'summary', header: 'Summary', cell: (row) => row.summary },
    {
      key: 'for',
      header: 'For',
      cell: (row) => (row.entity ? <Link to={href(row.drillDown)}>{row.entity.label}</Link> : '—'),
    },
  ]
}

/** Automation > Activity: what every workflow has done, most recent first. */
export function ActivityPage() {
  const notLive = useNotLivePage('automationActivity')
  const activity = useAutomationQuery('activity', (client) => client.automationActivity())
  const href = useDrillDown()
  const search = useFilterSearch()
  const now = useNow()

  const rows = activity.data?.data ?? []

  if (notLive) return <NotLivePage />

  return (
    <div className="ci-stack">
      <Card title="Activity" subtitle="What ran, when, and what it did." busy={activity.isFetching}>
        {activity.isPending ? <LoadingBlock rows={6} /> : null}
        {activity.isError ? <EmptyState variant="outage" compact description="Activity could not be loaded." /> : null}
        {activity.data ? (
          <DataTable
            caption="Automation activity"
            columns={columns(now, href, search)}
            rows={rows}
            rowKey={(row) => row.id}
            initialSort={{ key: 'at', direction: 'desc' }}
            empty={<EmptyState variant="no-data" compact title="No activity yet" description="Runs from active workflows will appear here." />}
          />
        ) : null}
      </Card>
    </div>
  )
}
